import type { QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

type TFn = (k: string, opts?: Record<string, unknown>) => string;

/**
 * Map a qbot coach error to a localized message under the given `cr` i18n
 * sub-namespace (`analysis.error.*` or `matchup.error.*`). Shared by every
 * embedded coach surface so the QUOTA_EXCEEDED / TIER_REQUIRED / INVALID_DECK
 * handling stays consistent.
 */
export function coachAnalysisError(err: unknown, t: TFn, ns: 'analysis' | 'matchup' = 'analysis'): string {
  if (!isQbotApiError(err)) return t(`${ns}.error.generic`);
  const code = err.data?.error?.code;
  if (code === 'QUOTA_EXCEEDED') {
    const details = err.data?.error?.details as { retryAfterSeconds?: number } | undefined;
    const seconds = details?.retryAfterSeconds ?? 0;
    return t(`${ns}.error.quota`, { minutes: Math.max(1, Math.ceil(seconds / 60)) });
  }
  if (code === 'TIER_REQUIRED') return t(`${ns}.error.tier`);
  if (code === 'INVALID_DECK' && ns === 'analysis') return t('analysis.error.noDeck');
  return t(`${ns}.error.generic`);
}
